import { useEffect, useRef, useState } from "react";
import { Check, BookOpen } from "lucide-react";
import { ActionButton, SecondaryAction } from "./kit";
import ReflectionPrompt from "./ReflectionPrompt";
import LogMatchSavedRating from "./LogMatchSavedRating";
import { facilityOptionLabel } from "../lib/facilities";
import { getVoiceStatus } from "../lib/voice";
import { counterText, NOT_RECORDED } from "../lib/counters";
import {
  PROGRESS_STEP_1,
  PROGRESS_STEP_2,
  PROGRESS_STEP_3,
  EYEBROW,
  LEDE,
  BATTING_LABEL,
  BOWLING_LABEL,
  RUNS_LABEL,
  BALLS_LABEL,
  OUT_LABEL,
  OVERS_LABEL,
  WKTS_LABEL,
  DID_NOT_BOWL_NOTE,
  REFLECT_BADGE,
  REFLECT_HEADING,
  REFLECT_SUB,
  REFLECT_HELP,
  REFLECT_CTA,
  SKIP_LABEL,
  SKIP_SUB,
  titleFor,
  buildHoldsSentence,
  bowledNothing,
} from "../lib/logSavedStep";

/**
 * `Saved.dc.html`'s three white pills — "1 Ground"/"2 The figures" ticked,
 * "3 A reflection" navy-filled and current (`log-step-3-progress`). Plain
 * text pills, not a bar — `StepCounter`'s own header already states no
 * drawn instance of this flow uses one.
 */
function ProgressPills() {
  const doneStyle = {
    display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
    background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-pill)",
  };
  const currentStyle = {
    display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
    background: "var(--nw-navy)", border: "1px solid var(--nw-navy)", borderRadius: "var(--nw-radius-pill)",
  };
  return (
    <div data-testid="log-step-3-progress" style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={doneStyle}>
        <Check size={12} strokeWidth={3.5} style={{ color: "var(--nw-ok)" }} aria-hidden="true" />
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>{PROGRESS_STEP_1}</span>
      </div>
      <div style={doneStyle}>
        <Check size={12} strokeWidth={3.5} style={{ color: "var(--nw-ok)" }} aria-hidden="true" />
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>{PROGRESS_STEP_2}</span>
      </div>
      <div style={currentStyle}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF" }}>{PROGRESS_STEP_3}</span>
      </div>
    </div>
  );
}

/**
 * The echo's own tile — bordered box, label above (`Saved.dc.html:91-99`).
 *
 * NOT the kit `NumberTile`: none of its variants draw THIS shape (a label
 * ABOVE a bordered, centred box) — its `read` variant (the issue text's own
 * "composed of the kit's `NumberTile variant=\"read\"` tiles") puts the
 * label and figure on one baseline-aligned row instead, which is a
 * different drawn composition on a different screen. Same class of
 * departure `DerivedRateRow` took in `LogMatchScorecardStep.jsx` (#758) —
 * flagged in the PR, not silently worked around. The dash colour here is
 * ALSO a deliberate divergence from `NumberTile`'s own convention:
 * `Saved.dc.html:97-99` draws an absent bowling figure at `--nw-ink-muted`
 * (`#64748B`), not `NumberTile`'s `--nw-ink-faint` (`#94A3B8`) — the
 * drawing wins.
 */
function EchoTile({ label, value, kind = "figure", "data-testid": testId }) {
  const isAbsent = value === NOT_RECORDED;
  const figureStyle = kind === "figure"
    ? { fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 20 }
    : { fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: 14 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>{label}</div>
      <div
        data-testid={testId}
        style={{
          minHeight: "var(--nw-size-control)",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)",
          color: isAbsent ? "var(--nw-ink-muted)" : "var(--nw-ink)",
          ...figureStyle,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
      {children}
    </div>
  );
}

/**
 * Issue #759 — step 3 of 3, the saved step. Matched against `Saved.dc.html`
 * (design-round-5, sha256 `20b26ed3…`) — see `brief-759.md` for where the
 * issue text and the drawing were reconciled.
 *
 * `match` is the `POST /matches` 201 body verbatim (never a re-fetch, never
 * form state — issue #759 §1). `matchesCount` is `GET /stats/summary`'s own
 * `matches`, fetched once on save. `seasonGoal` is `vision.short_term_goal`
 * from `GET /players/me/identity`, or `""` when the player has not said
 * (or the identity fetch failed — the same "never a fabricated sentence"
 * default either way).
 *
 * `reflectFor`/`onReflectionDone` thread the SAME `ReflectionPrompt` (#216)
 * the shipped page renders inline — brief-759.md's ruling 2: the drawing
 * puts `match-reflect-prompt` on the gold "Write the reflection"
 * (`Saved.dc.html:124`); pressing it reveals the existing body/save/skip
 * IN PLACE (the drawn "opened" state does not exist — brief-759.md says to
 * "keep it plain and within the visual system"). The drawn "Add a
 * reflection" card (the numbered badge, heading, sub-line and help line)
 * is ALWAYS rendered once `reflectFor` exists and never moves or
 * duplicates (PR #844 review, B1). While open, `ReflectionPrompt`'s own
 * `variant="kit"` (no card/icon/heading of its own — see that component's
 * header) swaps in UNDERNEATH the card's own content, still inside it; its
 * root carries a DIFFERENT, build-only testid, `match-reflect-form` — not
 * the same one, precisely because the drawing accents `match-reflect-prompt`
 * gold and the opened form is not gold, so token-check's accent rule must
 * find nothing live under that testid there rather than a non-gold element
 * wrongly claiming it (see `ReflectionPrompt.jsx`'s own comment on this).
 * While closed, the gold entry button (`match-reflect-prompt`) is a SIBLING
 * BELOW the card, not a piece of it — issue #871 (audit G8, D174 V1):
 * `Saved.dc.html:115-124` draws the card closing at `:122` and the button
 * following at `:124`, and #913 (D152) had left it nested inside instead.
 * J09 (PR #844 review, A3 final round) branches its own "did it close"
 * assertions by leg accordingly: `match-reflect-prompt` on flag-off
 * (identical to `origin/main`), `match-reflect-body` on flag-on.
 */
// D152 (#884) — `seasonGoal` is no longer read here (the quote-back is
// gone, see below); left as a no-op prop rather than removed from every
// caller, since `PerformanceTracking.jsx` (#904's own file) still fetches
// and passes it, and a future structured-goal feature may read it again.
export default function LogMatchSavedStep({ match, facilityKind, matchesCount, reflectFor, onReflectionDone, onSkip }) {
  const [reflectOpen, setReflectOpen] = useState(false);
  // Issue #851 — fetched ONCE per screen when the reflection opens, not
  // per box (this step has one box): `DictationControl` reads it to know
  // whether to render a mic at all, and whether the pilot's daily cap is
  // already spent (state 11a, "the mic is off before recording"). Any
  // failure already resolves to `{ available: false }` inside
  // `getVoiceStatus` itself, so there is no separate error branch here.
  const [voiceStatus, setVoiceStatus] = useState(null);
  useEffect(() => {
    if (!reflectOpen) return;
    let cancelled = false;
    getVoiceStatus().then((s) => {
      if (!cancelled) setVoiceStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, [reflectOpen]);
  // Issue #935 (WCAG 2.4.3) — where focus goes once the reflection closes.
  // `onReflectionDone` (the parent's) always clears `reflectFor`, so BOTH
  // the card and the gold `match-reflect-prompt` unmount the instant this
  // runs (see the `reflectFor?.match_id` guards below) — `log-saved-skip`
  // is the one thing left in the page stack to receive focus, for "Not
  // now" and a successful save alike.
  const skipRef = useRef(null);

  const handleReflectionDone = () => {
    onReflectionDone();
    // After the next paint: the card/button unmount and `log-saved-skip`
    // re-renders in this same tick, but its focus() would otherwise race
    // that DOM update.
    requestAnimationFrame(() => skipRef.current?.focus());
  };

  const m = match || {};
  const batting = m.batting || {};
  const bowling = m.bowling || {};
  const didNotBowl = bowledNothing(bowling);

  const holdsSentence = buildHoldsSentence({
    matches: matchesCount,
    runs: batting.runs,
    balls: batting.balls_faced,
    dismissal: batting.dismissal_type,
    ground: m.ground,
  });

  return (
    <div data-dc-id="log-step-3" data-testid="log-step-3" className="nw-page-stack" style={{ padding: "var(--nw-space-page)", color: "var(--nw-ink)" }}>
      <ProgressPills />

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Check size={18} strokeWidth={2.5} style={{ color: "var(--nw-ok)" }} aria-hidden="true" />
          <div className="nw-eyebrow">{EYEBROW}</div>
        </div>
        <div data-testid="log-step-3-title" className="nw-title">{titleFor(matchesCount)}</div>
        <div className="nw-lede">{LEDE}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "var(--nw-space-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
        {/* PR #844 review, B5 — the date used to be a flex sibling with no
            shrink/wrap protection of its own: `justify-content:
            space-between` on the row let a long ground name push it down
            to a shrunk, mid-word-broken 61px column ("2026-\n09-24").
            `minWidth: 0` on the ground name is what actually lets IT wrap
            instead (a flex item's default `min-width: auto` refuses to
            shrink below its own content width otherwise); the date's own
            `flexShrink: 0` + `whiteSpace: "nowrap"` is its own guarantee,
            not reliant on the ground name's behaviour to stay correct. */}
        <div data-testid="log-saved-echo" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{counterText(m.ground)}</div>
            {/* #1029 (#842 part B) — the field or net picked on step 2
                (`SavedRating.dc.html`'s `log-saved-facility`); nothing when
                none was picked. */}
            {m.facility_name && (
              <div data-testid="log-saved-facility" style={{ fontSize: 13, color: "var(--nw-ink-lede)" }}>
                {facilityOptionLabel({ name: m.facility_name, surface: m.facility_surface })}
              </div>
            )}
          </div>
          <div style={{ fontSize: 13, color: "var(--nw-ink-muted)", flexShrink: 0, whiteSpace: "nowrap" }}>{counterText(m.match_date)}</div>
        </div>
        <div style={{ height: 1, background: "var(--nw-line)" }} />

        <SectionLabel>{BATTING_LABEL}</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <EchoTile label={RUNS_LABEL} value={counterText(m.batting?.runs)} data-testid="log-saved-runs" />
          <EchoTile label={BALLS_LABEL} value={counterText(m.batting?.balls_faced)} data-testid="log-saved-balls" />
          <EchoTile label={OUT_LABEL} value={counterText(m.batting?.dismissal_type)} kind="text" data-testid="log-saved-out" />
        </div>

        <SectionLabel>{BOWLING_LABEL}</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <EchoTile label={OVERS_LABEL} value={counterText(m.bowling?.overs)} data-testid="log-saved-overs" />
          <EchoTile label={RUNS_LABEL} value={counterText(m.bowling?.runs_conceded)} data-testid="log-saved-bowling-runs" />
          <EchoTile label={WKTS_LABEL} value={counterText(m.bowling?.wickets)} data-testid="log-saved-wkts" />
        </div>
        {didNotBowl && (
          <div className="nw-help" data-testid="log-saved-not-bowled">{DID_NOT_BOWL_NOTE}</div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 48, padding: "10px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
        {/* PR #844 review, B4 — `lucide-react`'s `size` prop sets both
            width/height, but with no `flexShrink: 0` this flex row's own
            cross-axis squeeze (the holds sentence can run to two lines)
            shrank the icon to ~9-10px instead of the drawn 18x18. */}
        <BookOpen size={18} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />
        <span data-testid="log-saved-holds" style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink)" }}>{holdsSentence}</span>
      </div>

      {/* #1029 (#842 part B) — the optional rating of the picked field or
          net, with its own save; renders nothing without a `facility_id`. */}
      <LogMatchSavedRating match={m} facilityKind={facilityKind} />

      {/* D152 (#884, Andreas, 2026-09-25, decisions-2026-09-24.md): the
          season-goal quote-back (`log-saved-goal`, both the has-goal and
          no-goal states — Saved.dc.html:111/133, design-round-5) is
          dropped from this step. A "goal achieved" moment waits until
          goals are structured; this supersedes the 18 Sep §2 ruling that
          put the quote-back here. `seasonGoal` stays a prop (its fetch in
          `PerformanceTracking.jsx` is untouched — #904 owns that file) in
          case a future structured-goal feature reads it again. */}

      {/* PR #844 review, B1 — this card used to be TWO siblings: the drawn
          "Add a reflection" card (badge/heading/sub/help, navy border),
          then either the gold button or the WHOLE opened `ReflectionPrompt`
          — which was the ORIGINAL shadcn Card (gold-tint background/
          border, a NotebookPen icon, a second "Match logged vs…" heading,
          rem-based shadcn type), rendered as a SEPARATE card below the
          first. That failed token-check on the opened state (5 findings —
          none of `#0F1729`/`#E1E7EF` is a declared round-5 token) and, per
          the review, was never what the brief's "opens the existing body,
          save and skip in place" asked for: two different cards, not one
          continuing card. Now ONE card, ALWAYS the drawn shape — the
          badge/heading/sub/help never move or duplicate.

          Issue #871 (audit G8, D174 V1) — the OPENED piece underneath
          (`ReflectionPrompt`'s own `variant="kit"`, no card/icon/heading of
          its own — see that component's header) still swaps in place,
          INSIDE this card, unchanged. The CLOSED piece — the gold entry
          button — does not: `Saved.dc.html:115-124` (design-baseline-
          round-5) draws it full-width BELOW the card, as a sibling in the
          page stack, not inside it (the card there closes at `:122`, the
          button follows at `:124`). #913 (D152) removed the season-goal
          quote-back and left the button mis-nested; this is position only
          — the card's own badge/heading/sub/help are untouched.

          `log-saved-reflect-card` (PR #934 review, F1) is a BUILD-ONLY
          testid — the drawing never names this card, so structure-check
          reports it as ordinary drift, not a finding. It exists so J33's
          own behavioural assertion (the card and `match-reflect-prompt`
          share a parent, the card does not contain the button, and the
          button follows it in document order) survives a style tweak to
          the card, an extraction into its own component, or a self-
          closing div — none of which the source-level `<div>`-depth pin
          in `LogMatchSavedStep.test.js` can see past, and none of which
          say anything about where the button actually renders. */}
      {reflectFor?.match_id && (
        <div data-testid="log-saved-reflect-card" style={{ display: "flex", flexDirection: "column", gap: 8, padding: "var(--nw-space-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-navy)", borderRadius: "var(--nw-radius-card)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 20, height: 20, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-navy)", color: "#FFFFFF", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {REFLECT_BADGE}
            </div>
            <SectionLabel>{REFLECT_HEADING}</SectionLabel>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}>{REFLECT_SUB}</div>
          <div className="nw-help">{REFLECT_HELP}</div>

          {reflectOpen && (
            <ReflectionPrompt
              key={reflectFor.match_id}
              match={reflectFor}
              onDone={handleReflectionDone}
              suppressFailureToast
              variant="kit"
              voiceStatus={voiceStatus || { available: false }}
            />
          )}
        </div>
      )}

      {reflectFor?.match_id && !reflectOpen && (
        // Round 5 conformance, D27 (issue #864) — `ActionButton`'s own
        // default label colour is near-black ink now (`Saved.dc.html:124`:
        // `#D4A017` fill, `#0F172A` label); no per-call override needed.
        // Issue #871 — a sibling of the card above, not a child of it
        // (`Saved.dc.html:115-124`); `.nw-page-stack`'s own 14px gap
        // (`--nw-space-stack`, `index.css:463`) gives it the drawn gap.
        <ActionButton
          data-testid="match-reflect-prompt"
          onClick={() => setReflectOpen(true)}
        >
          {REFLECT_CTA}
        </ActionButton>
      )}

      {/* Ruling 1 (brief-759.md) — a text link, navy 14px/600, centred, as
          drawn (`Saved.dc.html:127`) — NOT the issue's own
          `SecondaryAction variant="boxed"`. `variant="link"` is exactly
          that shape already; only the centring (the drawn column is
          `align-items: center`, `variant="link"`'s own default has no
          opinion on alignment) is added here, on the wrapping column. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
        <SecondaryAction
          ref={skipRef}
          variant="link"
          data-testid="log-saved-skip"
          onClick={onSkip}
          // D237: grows up only; the help line below would paint over a
          // symmetric box, so the bottom margin is not pulled back.
          style={{ marginBottom: 0 }}
        >
          {SKIP_LABEL}
        </SecondaryAction>
        <span className="nw-help">{SKIP_SUB}</span>
      </div>
    </div>
  );
}
